/**
 * HostelMetrics.jsx — AOS-UX-KPI-01
 * Hostel / Residence domain metric components.
 *
 * Generic KPI primitives (MetricCard, MetricCardGrid, PremiumHeroBanner,
 * DashboardPanel, QuickActionGrid) are now sourced from the canonical shared
 * DashboardMetrics library.  This file re-exports them for backward
 * compatibility with HostelDashboard.jsx and retains only hostel-domain-specific
 * presentational components (BookingsSummaryPanel, RecentBookingsList).
 *
 * NO duplicate component implementations live here.
 */
import React from "react";
import { BedDouble } from "lucide-react";
import { Badge } from "../ui/badge";

// ── Re-export shared primitives ───────────────────────────────────────────────
// HostelDashboard.jsx imports these from this file; the canonical
// implementations now live in DashboardMetrics to avoid duplication.
export {
  MetricCard,
  MetricCardGrid,
  PremiumHeroBanner,
  DashboardPanel,
  QuickActionGrid,
} from "../shared/DashboardMetrics";

// ── BookingsSummaryPanel ───────────────────────────────────────────────────────
/**
 * Hostel-specific booking status breakdown list with colored leading dots.
 * Designed for display inside a DashboardPanel (light background).
 */
export function BookingsSummaryPanel({ checkedIn, confirmed, reserved, total }) {
  const rows = [
    { label: "Checked In",   value: checkedIn,  dot: "bg-amber-400",   text: "text-amber-600 font-semibold" },
    { label: "Confirmed",    value: confirmed,  dot: "bg-emerald-400", text: "text-emerald-700 font-semibold" },
    { label: "Reserved",     value: reserved,   dot: "bg-blue-400",    text: "text-blue-700 font-semibold" },
    { label: "Total Active", value: total,      dot: "bg-slate-800",   text: "text-slate-900 font-bold" },
  ];
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-2.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${r.dot}`} />
            <span className="text-sm text-slate-500">{r.label}</span>
          </div>
          <span className={`text-sm tabular-nums ${r.text}`}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── RecentBookingsList ─────────────────────────────────────────────────────────
/**
 * Hostel-specific compact list of recent bookings — light background rows.
 * Each row navigates to the booking detail on click.
 *
 * @param {Array}    bookings      - From /api/v1/hostel/dashboard recent_bookings
 * @param {Object}   statusConfig  - Map of status → { label, color (Tailwind) }
 * @param {Function} onNavigate    - Called with the booking detail path string
 */
export function RecentBookingsList({ bookings, statusConfig, onNavigate }) {
  if (!bookings || bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-slate-400">
        <BedDouble className="w-7 h-7 mb-2 opacity-30" />
        <p className="text-sm">No recent bookings</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {bookings.map((b) => {
        const sc = (statusConfig && statusConfig[b.status]) || {
          label: b.status,
          color: "bg-slate-100 text-slate-600 border-slate-200",
        };
        return (
          <button
            key={b.id}
            data-testid={`recent-booking-${b.id}`}
            onClick={() => onNavigate(`/campus/hostel/bookings/${b.id}`)}
            className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-slate-200 text-left transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1765d1]/40"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 p-1.5 rounded-lg bg-amber-50">
                <BedDouble className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-[#1765d1] transition-colors">
                  {b.booking_reference}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {b.hostel_name} · Room {b.room_number}
                </p>
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1 ml-3">
              <span className="text-sm font-semibold text-amber-600 tabular-nums">
                ₹{(b.total_amount || 0).toLocaleString("en-IN")}
              </span>
              <Badge className={`text-xs border ${sc.color}`}>{sc.label}</Badge>
            </div>
          </button>
        );
      })}
    </div>
  );
}
